// User management page — admin-only.
//
// Lists every account, lets admin invite a new user (server returns a
// one-time temp password the admin pastes to the user), change role,
// deactivate, or reset password.

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

type User = {
  id: string;
  email: string;
  name: string | null;
  role: 'admin' | 'manager' | 'field' | 'viewer';
  phone: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

const ROLE_LABEL: Record<string, string> = {
  admin: 'Admin',
  manager: 'Manager',
  field: 'Field crew',
  viewer: 'Viewer',
};
const ROLE_HINT: Record<string, string> = {
  admin: 'Everything — user mgmt, force overrides, deletes',
  manager: 'Project work — permits, projects, dates, edits',
  field: 'Forward permit transitions, photo upload, mark crossings',
  viewer: 'Read-only',
};
const ROLE_COLOR: Record<string, string> = {
  admin:   '#7c2d12',
  manager: '#1e3a8a',
  field:   '#15803d',
  viewer:  '#475569',
};


const PERM_LABEL: Record<string, string> = {
  'grantor.intake': 'Take in requests',
  'grantor.review': 'Run reviews',
  'grantor.decide': 'Sign determinations',
};

// Who may take a request in, price it, and sign the result. Separation of duties is
// enforced from this table, so an office that cannot see it cannot audit its own
// controls - and the first time anyone learns the shape of it should not be a 403 in
// the middle of closing a matter.
function GrantorPermissions() {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Record<string, string[]> | null>(null);
  const [selfDecide, setSelfDecide] = useState<boolean | null>(null);

  const q = useQuery<any>({
    queryKey: ['grantor-permissions'],
    queryFn: () => api.get('/users/grantor-permissions'),
  });
  const save = useMutation({
    mutationFn: () => api.put('/users/grantor-permissions', {
      matrix: draft, allow_self_decide: selfDecide ?? undefined,
    }),
    onSuccess: () => {
      setDraft(null); setSelfDecide(null);
      qc.invalidateQueries({ queryKey: ['grantor-permissions'] });
    },
    onError: (e: any) => alert(String(e?.message ?? e)),
  });

  if (q.isLoading || !q.data) return null;
  const matrix: Record<string, string[]> = draft ?? q.data.matrix ?? {};
  const perms: string[] = q.data.permissions ?? [];
  const roles = Object.keys(matrix);
  const selfNow = selfDecide ?? q.data.allow_self_decide ?? false;
  const dirty = draft !== null || selfDecide !== null;

  const toggle = (role: string, perm: string) => {
    const next: Record<string, string[]> = JSON.parse(JSON.stringify(matrix));
    const have = next[role]?.includes(perm);
    next[role] = have ? next[role].filter((x) => x !== perm) : [...(next[role] ?? []), perm];
    setDraft(next);
  };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card__header">
        <span>Oversight permissions</span>
        {dirty && (
          <span style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={() => { setDraft(null); setSelfDecide(null); }}>
              Cancel
            </button>
            <button className="btn btn--primary" disabled={save.isPending} onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </span>
        )}
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Role</th>
              {perms.map((p) => <th key={p} style={{ textAlign: 'center' }}>{PERM_LABEL[p] ?? p}</th>)}
            </tr>
          </thead>
          <tbody>
            {roles.map((role) => (
              <tr key={role}>
                <td style={{ textTransform: 'capitalize', fontWeight: 600 }}>{role}</td>
                {perms.map((perm) => (
                  <td key={perm} style={{ textAlign: 'center' }}>
                    <input type="checkbox"
                           checked={matrix[role]?.includes(perm) ?? false}
                           onChange={() => toggle(role, perm)} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card__body" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        {perms.map((p) => (
          <div key={p}><b>{PERM_LABEL[p] ?? p}</b> — {q.data.notes?.[p]}</div>
        ))}
      </div>
      <div className="card__body" style={{ borderTop: '1px solid var(--border)' }}>
        <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: 'pointer' }}>
          <input type="checkbox" checked={selfNow} onChange={(e) => setSelfDecide(e.target.checked)} />
          <span>
            <b>Let a reviewer sign their own determination</b>
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Off by default, and it should stay off wherever there are two people. A
              determination signed by the person who produced the estimate carries no
              second opinion, and the document says so.
            </div>
          </span>
        </label>
      </div>
    </div>
  );
}

export default function UsersPage() {
  const qc = useQueryClient();
  // Distinct from reset: reset generates a password, this accepts one. The plaintext
  // goes straight into the request and is never echoed back.
  const setPwMut = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      api.post(`/users/${id}/set-password`, { password }),
    onSuccess: () => alert('Password set.'),
    onError: (e: any) => alert(String(e?.message ?? e)),
  });

  const navigate = useNavigate();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [credToast, setCredToast] = useState<{ email: string; password: string } | null>(null);

  // Pull the current viewer's identity so we can gate "you can't do this if
  // you're not admin" before the server bounces them.
  const { data: me } = useQuery<{ id: string; email: string; role: string }>({
    queryKey: ['me'],
    queryFn: () => api.get('/auth/me'),
  });

  // Bounce non-admins off this page entirely — they can't see anything here.
  useEffect(() => {
    if (me && me.role !== 'admin') navigate('/dashboard', { replace: true });
  }, [me, navigate]);

  const { data: usersData, isLoading } = useQuery<{ items: User[] }>({
    queryKey: ['users'],
    queryFn: () => api.get('/users'),
    enabled: me?.role === 'admin',
  });
  const users = (usersData?.items ?? []).filter(u => showInactive || u.is_active);

  const patchMut = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: any }) => api.patch(`/users/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });

  const resetMut = useMutation({
    mutationFn: (id: string) => api.post(`/users/${id}/reset-password`, {}) as Promise<any>,
    onSuccess: (data: any) => {
      setCredToast({ email: data.email, password: data.temp_password });
    },
  });

  if (me && me.role !== 'admin') return null;

  return (
    <>
      <div className="page__header">
        <div>
          <h1 className="page__title">Users</h1>
          <div className="page__subtitle">Accounts, roles, and who may sign</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            Show inactive
          </label>
          <button onClick={() => setInviteOpen(true)} style={{
            padding: '6px 14px', background: '#1e3a8a', color: '#fff',
            border: 0, borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: 'pointer',
          }}>+ Add user</button>
        </div>
      </div>

      <div style={{
        background: '#fff', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: '#f8fafc' }}>
            <tr>
              <th style={th}>Email</th>
              <th style={th}>Name</th>
              <th style={th}>Role</th>
              <th style={th}>Phone</th>
              <th style={th}>Active</th>
              <th style={th}>Joined</th>
              <th style={{ ...th, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={7} style={{ padding: 20, color: '#94a3b8', textAlign: 'center' }}>Loading…</td></tr>
            )}
            {!isLoading && users.length === 0 && (
              <tr><td colSpan={7} style={{ padding: 20, color: '#94a3b8', textAlign: 'center' }}>No users{showInactive ? '' : ' (show inactive?)'}</td></tr>
            )}
            {users.map(u => (
              <tr key={u.id} style={{ borderTop: '1px solid #f1f5f9', opacity: u.is_active ? 1 : 0.5 }}>
                <td style={td}>
                  <span style={{ fontWeight: 600 }}>{u.email}</span>
                  {u.id === me?.id && <span style={{ marginLeft: 6, fontSize: 10, padding: '1px 5px', background: '#dbeafe', color: '#1e40af', borderRadius: 3 }}>YOU</span>}
                </td>
                <td style={td}>{u.name ?? <i style={{ color: '#94a3b8' }}>—</i>}</td>
                <td style={td}>
                  <select
                    value={u.role}
                    disabled={patchMut.isPending || u.id === me?.id}
                    onChange={e => patchMut.mutate({ id: u.id, patch: { role: e.target.value } })}
                    style={{
                      fontSize: 11, padding: '3px 6px', borderRadius: 3,
                      border: `1px solid ${ROLE_COLOR[u.role]}`,
                      color: ROLE_COLOR[u.role], fontWeight: 700,
                      background: '#fff', cursor: u.id === me?.id ? 'not-allowed' : 'pointer',
                    }}
                    title={u.id === me?.id ? "Can't demote yourself — ask another admin" : ROLE_HINT[u.role]}
                  >
                    {(['admin','manager','field','viewer'] as const).map(r => (
                      <option key={r} value={r}>{ROLE_LABEL[r]}</option>
                    ))}
                  </select>
                </td>
                <td style={{ ...td, fontFamily: 'monospace', fontSize: 11, color: '#64748b' }}>{u.phone ?? '—'}</td>
                <td style={td}>
                  <span style={{
                    fontSize: 10, padding: '2px 8px', borderRadius: 999, fontWeight: 700,
                    background: u.is_active ? '#dcfce7' : '#e2e8f0',
                    color:      u.is_active ? '#166534' : '#475569',
                  }}>{u.is_active ? 'ACTIVE' : 'INACTIVE'}</span>
                </td>
                <td style={{ ...td, fontSize: 11, color: '#64748b' }}>
                  {new Date(u.created_at).toLocaleDateString()}
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <button
                    onClick={() => {
                      const pw = prompt(
                        `Set a password for ${u.email}

`
                        + 'At least 12 characters. It is hashed on the server and never stored '
                        + 'in plain text.');
                      if (pw == null) return;
                      if (pw.length < 12) { alert('At least 12 characters.'); return; }
                      setPwMut.mutate({ id: u.id, password: pw });
                    }}
                    disabled={setPwMut.isPending || !u.is_active}
                    style={btnSecondary}
                    title="Type a password for this user"
                  >Set password</button>
                  <button
                    onClick={() => resetMut.mutate(u.id)}
                    disabled={resetMut.isPending || !u.is_active}
                    style={btnSecondary}
                    title="Generate a new temp password and show it once"
                  >Reset password</button>
                  {u.is_active ? (
                    <button
                      onClick={() => {
                        if (u.id === me?.id) { alert("You can't deactivate yourself."); return; }
                        if (confirm(`Deactivate ${u.email}? They will not be able to log in.`)) {
                          patchMut.mutate({ id: u.id, patch: { is_active: false } });
                        }
                      }}
                      disabled={u.id === me?.id}
                      style={{ ...btnSecondary, color: '#7f1d1d', marginLeft: 4 }}
                    >Deactivate</button>
                  ) : (
                    <button
                      onClick={() => patchMut.mutate({ id: u.id, patch: { is_active: true } })}
                      style={{ ...btnSecondary, color: '#166534', marginLeft: 4 }}
                    >Reactivate</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <GrantorPermissions />

      {inviteOpen && (
        <InviteModal
          onClose={() => setInviteOpen(false)}
          onCreated={(email, password) => {
            qc.invalidateQueries({ queryKey: ['users'] });
            setInviteOpen(false);
            setCredToast({ email, password });
          }}
        />
      )}

      {credToast && (
        <CredsModal
          email={credToast.email}
          password={credToast.password}
          onClose={() => setCredToast(null)}
        />
      )}
    </>
  );
}

function InviteModal({ onClose, onCreated }: {
  onClose: () => void;
  onCreated: (email: string, tempPassword: string) => void;
}) {
  const [email, setEmail] = useState('');
  const [name, setName]   = useState('');
  const [role, setRole]   = useState<'admin'|'manager'|'field'|'viewer'>('field');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr]     = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () => api.post('/users', {
      email, name, role, phone: phone || null,
      // Omitted entirely when blank, so the server generates and shows one once.
      ...(password ? { password } : {}),
    }) as Promise<any>,
    onSuccess: (data: any) => onCreated(data.user.email, data.temp_password),
    onError: (e: any) => setErr(e?.message ?? 'create failed'),
  });

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.4)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 460, maxWidth: '95%', background: '#fff', borderRadius: 8, padding: 24, boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 14 }}>Invite a user</div>

        <label style={lbl}>Email</label>
        <input value={email} onChange={e => setEmail(e.target.value)} placeholder="name@example.com"
          style={inp} type="email" autoFocus />

        <label style={lbl}>Name</label>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Jane Smith" style={inp} />

        <label style={lbl}>Role</label>
        <select value={role} onChange={e => setRole(e.target.value as any)} style={inp}>
          <option value="admin">Admin — full access</option>
          <option value="manager">Manager — project + permit work</option>
          <option value="field">Field crew — forward permit moves, photo upload</option>
          <option value="viewer">Viewer — read-only</option>
        </select>

        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, margin: '10px 0 4px' }}>
          Password
        </label>
        <input type="text" value={password} onChange={e => setPassword(e.target.value)}
               placeholder="Leave blank to generate one" style={inp} autoComplete="new-password" />
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
          {password
            ? (password.length < 12
                ? 'At least 12 characters.'
                : 'This account will be created with the password you typed. It is never stored in plain text.')
            : 'Left blank, the server generates one and shows it to you once.'}
        </div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: -4, marginBottom: 10 }}>{ROLE_HINT[role]}</div>

        <label style={lbl}>Phone (optional)</label>
        <input value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 304 555 0123" style={inp} />

        <div style={{ marginTop: 8, padding: 10, background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 4, fontSize: 11, color: '#92400e' }}>
          We'll show you a one-time temp password to give the user. They should change it after first login.
        </div>

        {err && <div style={{ marginTop: 8, padding: 8, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 4, fontSize: 12, color: '#7f1d1d' }}>{err}</div>}

        <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnSecondary}>Cancel</button>
          <button
            onClick={() => { setErr(null); createMut.mutate(); }}
            disabled={!email || !name || createMut.isPending || (!!password && password.length < 12)}
            style={{
              ...btnSecondary,
              background: (email && name) ? '#1e3a8a' : '#cbd5e1', color: '#fff',
              border: 'none', cursor: (email && name) ? 'pointer' : 'not-allowed',
            }}
          >{createMut.isPending ? 'Creating…' : 'Create user'}</button>
        </div>
      </div>
    </div>
  );
}

function CredsModal({ email, password, onClose }: { email: string; password: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(password).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      <div onClick={e => e.stopPropagation()} style={{ width: 460, maxWidth: '95%', background: '#fff', borderRadius: 8, padding: 24, boxShadow: '0 20px 50px rgba(0,0,0,0.25)' }}>
        <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>One-time password</div>
        <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.5, marginBottom: 12 }}>
          Send this to <b>{email}</b> through whatever channel you trust. We don't store the plaintext —
          if you close this dialog without copying, you'll need to reset the password.
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <code style={{ flex: 1, padding: '10px 14px', background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 16, fontFamily: 'ui-monospace, monospace', letterSpacing: '.04em' }}>
            {password}
          </code>
          <button onClick={copy} style={{
            padding: '10px 14px', fontSize: 13, fontWeight: 600,
            background: copied ? '#10b981' : '#1e3a8a', color: '#fff',
            border: 0, borderRadius: 4, cursor: 'pointer',
          }}>{copied ? '✓ Copied' : 'Copy'}</button>
        </div>
        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ ...btnSecondary, padding: '8px 16px' }}>Done</button>
        </div>
      </div>
    </div>
  );
}

const th: React.CSSProperties = { textAlign: 'left', padding: '10px 12px', fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '.04em' };
const td: React.CSSProperties = { padding: '10px 12px', verticalAlign: 'middle' };
const lbl: React.CSSProperties = { display: 'block', fontSize: 11, fontWeight: 600, color: '#475569', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 4 };
const inp: React.CSSProperties = { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid #cbd5e1', borderRadius: 4, marginBottom: 10 };
const btnSecondary: React.CSSProperties = { padding: '5px 10px', fontSize: 11, background: '#fff', border: '1px solid #cbd5e1', borderRadius: 3, cursor: 'pointer' };
